import React, {ForwardedRef, forwardRef, useImperativeHandle, useState} from 'react';
import Document from "../../../../models/documentModel";
import {ActivateHandle} from "../../../../util/interfaces";
import {createErrorMessage} from "../../../../util/utils";
import {ErrorType, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {ResponseData} from '@dvrd/fetch';
import DocumentData from "./documentData";

interface Props {
    onDocumentChanged: (document: Document, isNew: boolean, isDeleted: boolean) => void;
}

function DocumentDataController(props: Props, ref: ForwardedRef<ActivateHandle<Document>>) {
    const {onDocumentChanged} = props;
    const [document, setDocument] = useState(new Document());
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorType>(null);
    const [file, setFile] = useState<File | null>(null);

    function onActivate(document: Document) {
        setDocument(document);
        setActive(true);
    }

    function onClose() {
        setActive(false);
        window.setTimeout(() => {
            setDocument(new Document());
            setLoading(false);
            setError(null);
            setFile(null);
        });
    }

    function onPickFile(file: FileList) {
        setFile(file.item(0));
    }

    function onChangeLabel(value: string) {
        setDocument(document.clone().setField('label', value));
        setError(null);
    }

    function onChangeActive(value: boolean) {
        setDocument(document.clone().setField('active', value));
    }

    function onSubmit() {
        if (!validateValues()) return;
        setLoading(true);
        if (document.id.length) onUpdate();
        else onSave();
    }

    function onSave() {
        document.upload(file!, (document: Document, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                showSnackbar('Het document is toegevoegd');
                onDocumentChanged(document, true, false);
                onClose();
            } else showDialog(createErrorMessage(data.message ?? 'Het toevoegen van het document is niet gelukt.'),
                'Toevoegen mislukt');
        });
    }

    function onUpdate() {
        document.update((document: Document, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                showSnackbar('Het document is bijgewerkt');
                onDocumentChanged(document, false, false);
                onClose();
            } else showDialog(createErrorMessage(data.message ?? 'Het bijwerken van het document is niet gelukt.'),
                'Bijwerken mislukt');
        });
    }

    function onDeleteDocument() {
        showDialog(`Weet je zeker dat je het document '${document.label}' wilt verwijderen?`,
            'Document verwijderen',
            ['Nee', {label: 'Ja', onClick: onConfirmDeleteDocument, primary: false}]);
    }

    function onConfirmDeleteDocument() {
        setLoading(true);
        document.delete((document: Document, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                showSnackbar('Het document is verwijderd');
                onClose();
                onDocumentChanged(document, false, true);
            } else showDialog(
                createErrorMessage(data.message ?? 'Het verwijderen van het document is niet gelukt.'),
                'Verwijderen mislukt');
        })
    }

    function validateValues(): boolean {
        if (!document.label) {
            setError('Vul de naam van het document in');
            return false;
        } else if (!document.id.length && !file) {
            setError('Kies een bestand om toe te voegen');
            return false;
        }
        return true;
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <DocumentData onClose={onClose} onPickFile={onPickFile} onChangeLabel={onChangeLabel}
                      onChangeActive={onChangeActive} onSubmit={onSubmit} onDeleteDocument={onDeleteDocument}
                      document={document} active={active} loading={loading} error={error} file={file}/>
    );
}

export default forwardRef<ActivateHandle<Document>, Props>(DocumentDataController);